import { useMutation } from '@apollo/client';
import { UpdateApproverResponsesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpsertApproverResponsesTRPC } from './useUpsertApproverResponsesTRPC';

/**
 * Caller-facing input — the active approval LEVEL the actor is responding to.
 * The actor's approver-slot resolution happens SERVER-SIDE (tRPC service /
 * legacy Hasura action), never in the component, so callers pass only the
 * `levelId`.
 */
export interface SubmitApproverResponseInput {
  changeRequestId: string;
  parentId: string;
  levelId: string;
  approved: boolean;
  comment: string;
}

/**
 * Flag-gated approver-response submit (RSP-6087). Consumers import THIS — not
 * the `*TRPC` variant. Both paths take the `levelId` and resolve the actor's
 * eligible approver slot(s) server-side: `trpc` on → tRPC
 * `changeRequest.recordApproverResponse`; off → the legacy Apollo
 * `updateApproverResponses` action. Errors reject (no toast here) — the
 * calling panel surfaces them, matching the rule that data hooks don't own UI
 * side-effects.
 */
export const useUpsertApproverResponses = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateResponsesGraphQL, graphqlState] = useMutation(
    UpdateApproverResponsesDocument,
    {
      update: (cache) => {
        evictField(cache, 'change_request_by_pk');
      },
    }
  );

  const trpcHook = useUpsertApproverResponsesTRPC();

  const submitApproverResponse = async (
    input: SubmitApproverResponseInput
  ): Promise<void> => {
    if (trpcEnabled) {
      await trpcHook.recordApproverResponse(input);

      return;
    }

    await updateResponsesGraphQL({
      variables: {
        input: {
          ChangeRequestId: input.changeRequestId,
          Response: input.approved,
          Comment: input.comment,
          OverrideLevel: false,
          LevelId: input.levelId,
        },
      },
    });
  };

  return {
    submitApproverResponse,
    loading: trpcEnabled ? trpcHook.loading : graphqlState.loading,
  };
};
